/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Modal from '../../../../../components/Modal/Modal';
import { PrimaryButton, GhostButton } from '../../../../../styles/ui/Button';
import { FormGrid } from '../../../../../styles/layout/FormGrid';
import { Field, RequiredMark } from '../../../../../styles/forms/Field';
import { createRequirement } from '../../../../../services/hr/jobPositionRequirementsApi';
import { listDocumentTypes } from '../../../../../services/hr/documentTypesApi';
import { REQUIREMENT_TYPE_OPTIONS } from '../../constants';
import { readApiError, recruitmentErrorMessage } from '../../utils/apiError';

// ---------------------------------------------------------------------------
// Novo requisito do cargo. Tipo DOCUMENT (default — o único que dirige o
// checklist de admissão hoje) exige um tipo de documento do catálogo
// scope=HR_ADMISSION; os demais tipos são fundação e exigem um rótulo livre.
// ---------------------------------------------------------------------------

const FieldError = styled.span`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.danger ?? '#b71c1c'};
   margin-top: 2px;
`;

const EMPTY_FORM = {
   requirement_type: 'DOCUMENT',
   document_type_id: '',
   label: '',
   is_mandatory: true,
   notes: '',
};

export default function RequirementFormModal({
   isOpen,
   onClose,
   jobPositionId,
   onSaved,
}) {
   const [form, setForm] = useState(EMPTY_FORM);
   const [fieldErrors, setFieldErrors] = useState({});
   const [isSubmitting, setIsSubmitting] = useState(false);

   const [documentTypes, setDocumentTypes] = useState([]);
   const [typesLoaded, setTypesLoaded] = useState(false);

   const isDocument = form.requirement_type === 'DOCUMENT';

   useEffect(() => {
      if (isOpen) {
         setForm(EMPTY_FORM);
         setFieldErrors({});
         setIsSubmitting(false);
      }
   }, [isOpen]);

   // Catálogo de tipos de admissão — 1 fetch, no primeiro open.
   useEffect(() => {
      if (!isOpen || typesLoaded) return;
      listDocumentTypes({ scope: 'HR_ADMISSION' })
         .then((types) => {
            setDocumentTypes(types);
            setTypesLoaded(true);
         })
         .catch(() => {
            toast.error(
               'Erro ao carregar os tipos de documento. Reabra o modal.',
            );
         });
   }, [isOpen, typesLoaded]);

   const handleChange = (event) => {
      const { name, value, type, checked } = event.target;
      setForm((prev) => ({
         ...prev,
         [name]: type === 'checkbox' ? checked : value,
      }));
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
   };

   const handleConfirm = async () => {
      if (isSubmitting) return;

      if (isDocument && !form.document_type_id) {
         setFieldErrors({ document_type_id: 'Selecione o tipo de documento.' });
         return;
      }
      if (!isDocument && !form.label.trim()) {
         setFieldErrors({ label: 'Informe o rótulo do requisito.' });
         return;
      }

      const payload = {
         requirement_type: form.requirement_type,
         is_mandatory: form.is_mandatory,
      };
      if (isDocument) payload.document_type_id = Number(form.document_type_id);
      if (form.label.trim()) payload.label = form.label.trim();
      if (form.notes.trim()) payload.notes = form.notes.trim();

      setIsSubmitting(true);
      setFieldErrors({});
      try {
         const { message } = await createRequirement(jobPositionId, payload);
         toast.success(message);
         onSaved?.();
         onClose?.();
      } catch (err) {
         const { fieldErrors: apiFieldErrors } = readApiError(err);
         if (Object.keys(apiFieldErrors).length) {
            setFieldErrors(apiFieldErrors);
         } else {
            toast.error(
               recruitmentErrorMessage(err, 'Erro ao criar o requisito.'),
            );
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title="Novo requisito do cargo"
         maxWidth="560px"
         footer={
            <>
               <GhostButton
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
               >
                  Cancelar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={handleConfirm}
                  disabled={isSubmitting}
               >
                  {isSubmitting ? 'Salvando…' : 'Criar requisito'}
               </PrimaryButton>
            </>
         }
      >
         <FormGrid>
            <Field className="col-6">
               <label htmlFor="requirement-type">Tipo de requisito</label>
               <select
                  id="requirement-type"
                  name="requirement_type"
                  value={form.requirement_type}
                  onChange={handleChange}
                  disabled={isSubmitting}
               >
                  {REQUIREMENT_TYPE_OPTIONS.map((option) => (
                     <option key={option.value} value={option.value}>
                        {option.label}
                     </option>
                  ))}
               </select>
            </Field>

            {isDocument ? (
               <Field className="col-6">
                  <label htmlFor="requirement-document-type">
                     Documento exigido
                     <RequiredMark>*</RequiredMark>
                  </label>
                  <select
                     id="requirement-document-type"
                     name="document_type_id"
                     value={form.document_type_id}
                     onChange={handleChange}
                     disabled={isSubmitting}
                     aria-invalid={!!fieldErrors.document_type_id || undefined}
                  >
                     <option value="">
                        {typesLoaded ? 'Selecione…' : 'Carregando tipos…'}
                     </option>
                     {documentTypes.map((documentType) => (
                        <option key={documentType.id} value={documentType.id}>
                           {documentType.name}
                        </option>
                     ))}
                  </select>
                  {fieldErrors.document_type_id && (
                     <FieldError>{fieldErrors.document_type_id}</FieldError>
                  )}
               </Field>
            ) : (
               <Field className="col-6">
                  <label htmlFor="requirement-label">
                     Rótulo
                     <RequiredMark>*</RequiredMark>
                  </label>
                  <input
                     id="requirement-label"
                     type="text"
                     name="label"
                     maxLength={120}
                     placeholder="Ex.: curso NR-35"
                     value={form.label}
                     onChange={handleChange}
                     disabled={isSubmitting}
                     aria-invalid={!!fieldErrors.label || undefined}
                  />
                  {fieldErrors.label && (
                     <FieldError>{fieldErrors.label}</FieldError>
                  )}
               </Field>
            )}

            <Field className="col-12">
               <label htmlFor="requirement-notes">Observações</label>
               <textarea
                  id="requirement-notes"
                  name="notes"
                  rows={2}
                  value={form.notes}
                  onChange={handleChange}
                  disabled={isSubmitting}
               />
            </Field>

            <Field className="col-12">
               <label
                  htmlFor="requirement-is-mandatory"
                  style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: '8px',
                     cursor: 'pointer',
                  }}
               >
                  <input
                     id="requirement-is-mandatory"
                     type="checkbox"
                     name="is_mandatory"
                     checked={form.is_mandatory}
                     onChange={handleChange}
                     disabled={isSubmitting}
                  />
                  Requisito obrigatório
               </label>
            </Field>
         </FormGrid>

         {!isDocument && (
            <p
               style={{
                  margin: '12px 0 0',
                  fontSize: '0.78rem',
                  color: '#6b7280',
               }}
            >
               Hoje, apenas requisitos do tipo <strong>Documento</strong>{' '}
               afetam o checklist de admissão — os demais são registro/fundação.
            </p>
         )}
      </Modal>
   );
}

RequirementFormModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   jobPositionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   onSaved: PropTypes.func,
};
